import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateParser } from '@ngx-translate/core';
import {
  CORE_AUTH_CONFIG,
  CORE_LAYOUT_MANAGE_IDB,
  createCoreAuthConfigFromArkikEnvironment,
  createCoreTranslateLoader,
  createCoreTranslateParser,
} from 'core';
import { CHATBOT_ROLE_LOOKUP, ChatbotRoleLookup, getTalkToYourDataApiBaseUrl } from '@certify';
import { ALLOWED_CERTIFY_ROLE_CODES } from './pages/certify-dashboard/domain/enums/certify-role.enum';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ManageIdbService } from './idb/manage-idb.service';
import { RoleRepositoryService } from './core/repositories/admin/roles/role-repository.service';
import { map } from 'rxjs/operators';
import { PROGRAMMATIC_COMPONENT_MODULES } from '@shared/constants/programmatic-component-modules';
import { LogoLoaderComponentModule } from '@shared/components/logo-loader/logo-loader-component.module';
import { CommonModule, DecimalPipe, DOCUMENT } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule, HttpClient } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
// eslint-disable-next-line no-restricted-imports -- allowed here since it's a place of global initialization
import { CmxWebComponentsModule } from '@cmx-web-components/angular';
import { SecurityEncryptService } from '@core/services/admin/login/security-encrypt.service';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { CookieService } from 'ngx-cookie-service';
import { IdleService } from './core/services/admin/security/idle.service';
import { Globals } from './pages/services/globals';
// MSAL and MSAL browser libraries
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
// Import the Azure AD B2C configuration
import { msalConfig, protectedResourceScopes } from './auth-config';
import { AuthRedirectComponent } from './auth-redirect.component';

import { PortalModule } from '@angular/cdk/portal';
import { TranslationService } from '@core/services/common/translation.service';
import { OrdersActionsService } from '@shared/services/order/orders-actions.service';
import { PouringPerformanceService } from '@shared/services/order/pouring-performance.service';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { environment } from '../environments/environment';
import { Location } from '@angular/common';

import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeEsMx from '@angular/common/locales/es-MX';
import localeEs from '@angular/common/locales/es';
import { en_US, NZ_I18N } from 'ng-zorro-antd/i18n';
import { ONLINE_INTERCEPTOR } from '@core/interceptors/online.interceptor';
import { JWT_INTERCEPTOR, JWTInterceptor } from '@core/interceptors/jwt.interceptor';
import { Router, UrlSerializer } from '@angular/router';
import { OfflineGuard } from '@core/guard/offline/offline.guard';
import { MsalLocalService } from '@core/services/common/msal-local.service';
import { CatalogLogoutService, protectCatalogLogout } from '@core/services/common/catalog-logout.service';
import { ConfiguratorModule } from '@core/modules/configurator/configurator.module';
import { EndpointConstants } from '@core/services/endpoints-configurations/endpoint-constants';
import { UI_UX_DARK_MODE_ENABLED } from '@shared/ui-ux-components/ui-ux-components.config';
import { ArkikStatusService } from '@core/services/common/arkik-status.service';
import { RetryInterceptor } from '@core/services/interceptor/interceptor.service';
import { StoreModule } from '@ngrx/store';
import { reducers } from './store/reducers';
import { appEffects } from './store/effects';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ARKIK_WEB_COMPONENTS_PROVIDERS } from '@core/modules';
import { REST_INFRASTRUCTURE_PROVIDERS } from '@core/providers/infrastructure-providers';
import { ArkikEchartsModule } from '@core/echarts';
import { NgxMaskModule } from 'ngx-mask';
import { PIPE_PROVIDERS } from '@core/providers/pipe-providers';
import { PLANXGO_TRANSLATIONS } from '@planxgo';
import { CERTIFY_TRANSLATIONS } from '@certify';
import { ModalContainerModule } from '@shared/components/modal-container/modal-container.module';
import { CREWS_TRANSLATIONS } from 'libs/crews/src/lib/i18n/crews.translations';
registerLocaleData(localeEsMx);
registerLocaleData(localeEn);
registerLocaleData(localeEs);

@NgModule({
  declarations: [AppComponent, AuthRedirectComponent],
  imports: [
    TranslateModule.forRoot({
      loader: { provide: TranslateLoader, useFactory: createCoreTranslateLoader, deps: [HttpClient] },
      parser: { provide: TranslateParser, useFactory: createCoreTranslateParser },
    }),
    LogoLoaderComponentModule,
    ...PROGRAMMATIC_COMPONENT_MODULES,
    ConfiguratorModule.forRoot(),
    CmxWebComponentsModule.forRoot(),
    ArkikEchartsModule.forRoot(),
    NgxMaskModule.forRoot({ validation: false }),
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    CommonModule,
    AppRoutingModule,
    NgIdleKeepaliveModule.forRoot(),
    MsalModule,
    LoggerModule.forRoot({
      level: !environment.production ? NgxLoggerLevel.LOG : NgxLoggerLevel.OFF,
    }),
    ModalContainerModule,
    PortalModule,
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot(appEffects),
    !environment.production ? StoreDevtoolsModule.instrument({ maxAge: 25 }) : [],
  ],
  providers: [
    ...ARKIK_WEB_COMPONENTS_PROVIDERS,
    ...REST_INFRASTRUCTURE_PROVIDERS,
    ...PIPE_PROVIDERS,
    {
      provide: APP_INITIALIZER,
      useFactory: initArkikNgxTranslate,
      deps: [TranslationService],
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: () => {
        return new PublicClientApplication(msalConfig);
      },
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: () => {
        return {
          interactionType: InteractionType.Redirect,
          authRequest: {
            scopes: protectedResourceScopes,
          },
        };
      },
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: () => {
        const protectedResourcesMap = new Map([
          [`${environment.APIM}arkik-customermanagement/v1/cum/inviteinfo`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/companyinfo`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/validateemail`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/updatepassword`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/createcompany`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/accountconfiguration`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/updateimagecompany`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/accountinfo`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/verifyteaminvite`, null],
          [`${environment.APIM}arkik-customermanagement/v1/ntf/onboard/validatestorage`, null],
          [`${environment.APIM}arkik-customermanagement/v1/ntf/onboard`, null],
          [`${environment.APIM}arkik-customermanagement/v1/ntf/onboard/logo`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/updatecompanynameb2cuser`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/validateSendingEmail`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/createaccount`, null],
          [`${environment.APIM}arkik-customermanagement/v1/cum/postupdateinvite`, null],
          [`${environment.APIM}arkik-planning/api/planning/driver-callout-notifications`, null],
          [`${environment.APIM}arkik-ordertaking/api/Public/Order/OrderDetailByOrderTakingFormat`, null],
          [`${environment.APIM}arkik-ordertaking/api/Public/Order/OrderDetail`, null],
          [`${environment.APIM}arkik-ordertaking/api/Public/Order/ChangeOrderStatus`, null],
          [`${environment.APIM}arkik-ordertaking/api/Public/CancellationReason/GetCancelReason`, null],
          [`${environment.APIM}arkik-ordertaking/api/Public/Purchaser`, null],
          [`${environment.APIM}arkik-record/Public/byLoggedUser`, null],
          [`${environment.APIM}storeprospects`, null],
          [EndpointConstants.ApiEdgeSettings, null],
          [`${getTalkToYourDataApiBaseUrl(environment.APIM)}`, protectedResourceScopes],
          [environment.APIM, protectedResourceScopes],
          [environment.apiManagmet, protectedResourceScopes],
          ['https://localhost', protectedResourceScopes],
        ]);

        return {
          interactionType: InteractionType.Redirect,
          protectedResourceMap: protectedResourcesMap,
        };
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useFactory: (
        msalInterceptorConfig: MsalInterceptorConfiguration,
        authService: MsalService,
        location: Location,
        document: any,
        arkikStatusService: ArkikStatusService,
      ) => {
        const isLocalWeb = environment.isLocalWeb;

        return isLocalWeb && !arkikStatusService.isArkikOnlineCapable()
          ? new JWTInterceptor(arkikStatusService)
          : new MsalInterceptor(msalInterceptorConfig, authService, location, document);
      },
      deps: [MSAL_INTERCEPTOR_CONFIG, MsalService, Location, DOCUMENT, ArkikStatusService],
    },
    {
      provide: MsalService,
      useFactory: (
        instance: IPublicClientApplication,
        location: Location,
        router: Router,
        arkikStatusService: ArkikStatusService,
        catalogLogout: CatalogLogoutService,
      ) => {
        const isLocalWeb = environment.isLocalWeb;

        const service =
          isLocalWeb && !arkikStatusService.isArkikOnlineCapable()
            ? new MsalLocalService(router, arkikStatusService)
            : new MsalService(instance, location);
        return protectCatalogLogout(service, catalogLogout);
      },
      deps: [MSAL_INSTANCE, Location, Router, ArkikStatusService, CatalogLogoutService],
    },
    Globals,
    CookieService,
    IdleService,
    SecurityEncryptService,
    {
      provide: MsalGuard,
      useFactory: (
        msalGuardConfig: MsalGuardConfiguration,
        msalBroadcastService: MsalBroadcastService,
        authService: MsalService,
        location: Location,
        router: Router,
        urlSerializer: UrlSerializer,
        arkikStatusService: ArkikStatusService,
      ) => {
        const isLocalWeb = environment.isLocalWeb;

        return isLocalWeb && !arkikStatusService.isArkikOnlineCapable()
          ? new OfflineGuard(urlSerializer, arkikStatusService)
          : new MsalGuard(msalGuardConfig, msalBroadcastService, authService, location, router);
      },
      deps: [MSAL_GUARD_CONFIG, MsalBroadcastService, MsalService, Location, Router, UrlSerializer, ArkikStatusService],
    },
    OrdersActionsService,
    DecimalPipe,
    PouringPerformanceService,
    { provide: NZ_I18N, useValue: en_US },
    {
      provide: LOCALE_ID,
      deps: [TranslationService],
      useFactory: (ts: TranslationService) => ts.getLocale(),
    },
    JWT_INTERCEPTOR,
    ONLINE_INTERCEPTOR,
    JWT_INTERCEPTOR,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true,
    },
    { provide: UI_UX_DARK_MODE_ENABLED, useValue: true },
    ONLINE_INTERCEPTOR,
    JWT_INTERCEPTOR,
    // Value-only: lets the lazy-loaded crews module resolve CoreAuthTokenService/CrewsApiService DI
    // without registering CoreAuthModule's own MSAL instance/interceptors (this app has its own).
    { provide: CORE_AUTH_CONFIG, useValue: createCoreAuthConfigFromArkikEnvironment(environment) },
    { provide: CORE_LAYOUT_MANAGE_IDB, useExisting: ManageIdbService },
    {
      provide: CHATBOT_ROLE_LOOKUP,
      useFactory: (roleRepository: RoleRepositoryService): ChatbotRoleLookup => ({
        getRoleCode$: () =>
          roleRepository.getUserRolesAuth().pipe(
            map((roles) => {
              const certifyRole = roles?.find((role) =>
                ALLOWED_CERTIFY_ROLE_CODES.includes(
                  String(role.RoleCode || '')
                    .trim()
                    .toUpperCase(),
                ),
              );

              return certifyRole?.RoleCode || roles?.[0]?.RoleCode || null;
            }),
          ),
      }),
      deps: [RoleRepositoryService],
    },
  ],
  bootstrap: [AppComponent, AuthRedirectComponent],
})
export class AppModule {}

export function initArkikNgxTranslate(translationService: TranslationService): () => void {
  return () => {
    translationService.registerLibraryTranslations(PLANXGO_TRANSLATIONS);
    translationService.registerLibraryTranslations(CERTIFY_TRANSLATIONS);
    translationService.registerLibraryTranslations(CREWS_TRANSLATIONS);
  };
}
